import React, { useEffect, useState } from 'react'
import './App.css'
import './FeesPage.css'
import { Link, Redirect } from 'react-router-dom'
import Header from './layouts/Header'

const FeeCard = ({ label, value, description, featured }) => (
  <article className={`kx-fee-card ${featured ? 'kx-fee-card--featured' : ''}`}>
    <div className="kx-fee-card__top">
      <span>{label}</span>
      {featured && <b>CORE</b>}
    </div>

    <strong>{value}</strong>

    <p>{description}</p>
  </article>
)

const FeatureItem = ({ children }) => (
  <li>
    <span>✓</span>
    <p>{children}</p>
  </li>
)

const FeesPage = () => {
  const [openSection, setOpenSection] = useState(null)

  useEffect(() => {
    document.title = 'Fees & Pricing - Karamale'
  }, [])

  if (localStorage.getItem('customername') !== null) {
    return <Redirect to="/customerdashboard" />
  }

  const toggleSection = (section) => {
    setOpenSection((current) => (current === section ? null : section))
  }

  const faqs = [
    {
      id: 'free',
      question: 'Is it free to create events?',
      answer: 'Yes. Creating an event on Karamale is completely free.',
    },
    {
      id: 'budget',
      question: 'What happens when my campaign budget runs out?',
      answer: 'Your event pauses until you add more funds to the campaign budget.',
    },
    {
      id: 'printed',
      question: 'Can I sell printed tickets only?',
      answer:
        'Yes. Karamale supports both printed and digital tickets, with fraud protection and scanning validation.',
    },
    {
      id: 'minimum',
      question: 'Is there a minimum campaign budget?',
      answer: 'Yes. You can activate a campaign with a minimum budget of R10.00.',
    },
  ]

  return (
    <div className="kx-fees-page">
      <Header />

      {/* HERO */}
      <section className="kx-fees-hero">
        <div className="kx-fees-hero__grid" />
        <div className="kx-fees-hero__orb" />

        <div className="kx-fees-wrap kx-fees-hero__inner">
          <div className="kx-fees-hero__copy">
            <div className="kx-fees-eyebrow">
              <span />
              KARAMALE PRICING
            </div>

            <h1>
              Simple pricing.
              <br />
              <strong>No surprises.</strong>
            </h1>

            <p>Create events for free, choose your campaign budget and keep your ticket revenue.</p>

            <div className="kx-fees-hero__actions">
              <Link to="/organiser-registration" className="kx-fees-btn kx-fees-btn--primary">
                Start selling
                <span>→</span>
              </Link>

              <a href="#organiser-fees" className="kx-fees-hero__link">
                See the pricing
                <span>↓</span>
              </a>
            </div>
          </div>

          <div className="kx-fees-hero__visual">
            <div className="kx-fees-hero__revenue">
              <span>YOUR TICKET REVENUE</span>

              <strong>100%</strong>

              <div className="kx-fees-hero__revenue-line">
                <span>REMAINS</span>
                <b>YOURS.</b>
              </div>
            </div>

            <div className="kx-fees-hero__mini">
              <span>CAMPAIGN SERVICE FEE</span>
              <strong>3.5%</strong>
              <small>deducted from campaign budget</small>
            </div>

            <div className="kx-fees-hero__mini kx-fees-hero__mini--bottom">
              <span>EVENT CREATION</span>
              <strong>FREE</strong>
              <small>create as many events as you need</small>
            </div>
          </div>
        </div>
      </section>

      {/* THREE PRINCIPLES */}
      <section className="kx-fees-principles">
        <div className="kx-fees-wrap">
          <div className="kx-fees-principles__intro">
            <span>THE KARAMALE MODEL</span>
            <h2>
              Three things
              <br />
              to remember.
            </h2>
          </div>

          <div className="kx-fees-principles__items">
            <div>
              <span>01</span>
              <strong>Create for free</strong>
              <p>There is no charge to create an event or prepare it for launch.</p>
            </div>

            <div>
              <span>02</span>
              <strong>Fund your campaign</strong>
              <p>Activate ticket sales by adding a campaign budget from R10.</p>
            </div>

            <div>
              <span>03</span>
              <strong>Keep your revenue</strong>
              <p>Karamale does not take a percentage of your ticket revenue.</p>
            </div>
          </div>
        </div>
      </section>

      <main className="kx-fees-main">
        <div className="kx-fees-wrap">
          {/* ORGANISER FEES */}
          <section id="organiser-fees" className="kx-fees-section kx-fees-section--pricing">
            <div className="kx-fees-section__heading">
              <div>
                <div className="kx-fees-eyebrow kx-fees-eyebrow--dark">
                  <span />
                  FOR ORGANISERS
                </div>

                <h2>
                  Know exactly
                  <br />
                  <strong>what you're paying for.</strong>
                </h2>
              </div>

              <p>
                Karamale uses a campaign-based model. Creating your event is free, while ticket
                activity, printing and payouts have clearly defined costs.
              </p>
            </div>

            <div className="kx-fee-grid">
              <FeeCard
                label="Event creation"
                value="Free"
                description="Create unlimited events whenever you need."
              />

              <FeeCard
                label="Campaign budget"
                value="R10+"
                description="Minimum budget required to activate ticket sales."
              />

              <FeeCard
                label="Campaign service fee"
                value="3.5%"
                description="Applied per ticket and deducted from your campaign budget."
                featured
              />

              <FeeCard
                label="Self-print ticket"
                value="R1.00"
                description="Download your ticket and print it yourself."
              />

              <FeeCard
                label="Karamale printing"
                value="R3.50"
                description="Karamale prints and prepares physical tickets."
              />

              <FeeCard
                label="Payout"
                value="Small fee"
                description="A small fee applies when funds are transferred to your account."
              />
            </div>

            <div className="kx-fees-revenue-note">
              <div className="kx-fees-revenue-note__mark">✓</div>

              <div>
                <strong>Your ticket revenue remains yours.</strong>
                <p>
                  The 3.5% service fee comes from your campaign budget — not from your ticket
                  revenue.
                </p>
              </div>
            </div>
          </section>

          {/* TICKET FORMATS */}
          <section className="kx-fees-section kx-fees-section--tickets">
            <div className="kx-fees-section__heading">
              <div>
                <div className="kx-fees-eyebrow kx-fees-eyebrow--dark">
                  <span />
                  TICKET FORMATS
                </div>

                <h2>
                  Digital,
                  <br />
                  <strong>physical, or both.</strong>
                </h2>
              </div>

              <p>
                Choose how your audience receives their tickets. Every format is protected and
                validated through Karamale.
              </p>
            </div>

            <div className="kx-ticket-table">
              <div className="kx-ticket-table__head">
                <span>FORMAT</span>
                <span>DELIVERY</span>
                <span>COST</span>
                <span>DETAILS</span>
              </div>

              <div className="kx-ticket-table__row">
                <div>
                  <strong>Digital</strong>
                  <small>Electronic ticket</small>
                </div>

                <div>Issued electronically</div>

                <div>
                  <strong>3.5%</strong>
                  <small>Campaign budget</small>
                </div>

                <div>Automatically deducted when the ticket is sold.</div>
              </div>

              <div className="kx-ticket-table__row">
                <div>
                  <strong>Physical</strong>
                  <small>Self-print</small>
                </div>

                <div>Download + print</div>

                <div>
                  <strong>R1.00</strong>
                  <small>Per ticket</small>
                </div>

                <div>Download the ticket and print it yourself.</div>
              </div>

              <div className="kx-ticket-table__row">
                <div>
                  <strong>Physical</strong>
                  <small>Karamale print</small>
                </div>

                <div>Printed by Karamale</div>

                <div>
                  <strong>R3.50</strong>
                  <small>Per ticket</small>
                </div>

                <div>Karamale prints and prepares tickets for distribution.</div>
              </div>
            </div>

            <div className="kx-fees-ticket-note">
              <span>✓</span>
              <p>All ticket formats include fraud protection and scanning validation.</p>
            </div>
          </section>

          {/* BUYERS */}
          <section className="kx-fees-buyers">
            <div className="kx-fees-buyers__intro">
              <div className="kx-fees-eyebrow">
                <span />
                FOR TICKET BUYERS
              </div>

              <h2>
                Clear pricing
                <br />
                <strong>at checkout.</strong>
              </h2>

              <p>
                Buyers see applicable fees before completing their purchase. No unexpected
                payment-processing charges are added afterwards.
              </p>
            </div>

            <div className="kx-fees-buyers__cards">
              <div>
                <span>01</span>
                <strong>Small booking fee</strong>
                <p>A small booking fee may be added at checkout.</p>
              </div>

              <div className="kx-fees-buyers__card--accent">
                <span>02</span>
                <strong>No processing surcharge</strong>
                <p>There are no additional payment-processing charges.</p>
              </div>

              <div>
                <span>03</span>
                <strong>Upfront pricing</strong>
                <p>Applicable fees are displayed before the buyer pays.</p>
              </div>
            </div>
          </section>

          {/* CAMPAIGN MODEL */}
          <section className="kx-fees-campaign-section">
            <div className="kx-fees-campaign">
              <div className="kx-fees-campaign__content">
                <div className="kx-fees-eyebrow">
                  <span />
                  THE CAMPAIGN MODEL
                </div>

                <h2>
                  Your budget
                  <br />
                  <strong>powers sales.</strong>
                </h2>

                <p>
                  Creating an event is free. When you're ready to activate ticket sales, you add
                  campaign funds.
                </p>

                <div className="kx-fees-campaign__big-number">
                  <span>STARTING FROM</span>
                  <strong>R10</strong>
                </div>
              </div>

              <div className="kx-fees-campaign__steps">
                <div>
                  <span>01</span>
                  <div>
                    <strong>Create your event</strong>
                    <p>No event creation fee.</p>
                  </div>
                </div>

                <div className="kx-fees-campaign__connector" />

                <div>
                  <span>02</span>
                  <div>
                    <strong>Add campaign funds</strong>
                    <p>Start with as little as R10.</p>
                  </div>
                </div>

                <div className="kx-fees-campaign__connector" />

                <div>
                  <span>03</span>
                  <div>
                    <strong>Sell tickets</strong>
                    <p>3.5% is deducted from your campaign budget.</p>
                  </div>
                </div>

                <div className="kx-fees-campaign__connector" />

                <div>
                  <span>04</span>
                  <div>
                    <strong>Top up when needed</strong>
                    <p>Your event pauses when the budget runs out.</p>
                  </div>
                </div>
              </div>
            </div>

            <div className="kx-fees-rollover">
              <strong>Unused campaign funds roll forward.</strong>
              <span>
                Remaining budget can be used for your next event. No refunds, but unused budget is
                not simply lost.
              </span>
            </div>
          </section>

          {/* PROMOTERS */}
          <section className="kx-fees-promoters">
            <div className="kx-fees-promoters__copy">
              <div className="kx-fees-eyebrow kx-fees-eyebrow--dark">
                <span />
                PROMOTER ACCOUNTS
              </div>

              <h2>
                Give your team
                <br />
                <strong>their own lane.</strong>
              </h2>

              <p>
                Create individual promoter accounts and give your sales team the ability to sell
                tickets on your behalf.
              </p>

              <Link to="/organiser-registration" className="kx-fees-inline-link">
                Become an organiser
                <span>→</span>
              </Link>
            </div>

            <div className="kx-fees-promoters__panel">
              <div className="kx-fees-promoters__panel-head">
                <span>PROMOTER ACCESS</span>
                <b>LIVE</b>
              </div>

              <ul>
                <FeatureItem>Create unique login credentials for each promoter</FeatureItem>

                <FeatureItem>Sell tickets directly on your behalf</FeatureItem>

                <FeatureItem>Track sales and earnings</FeatureItem>

                <FeatureItem>View category-level sales breakdowns</FeatureItem>

                <FeatureItem>Deactivate or delete promoter accounts</FeatureItem>
              </ul>
            </div>
          </section>

          {/* FAQ */}
          <section className="kx-fees-faq">
            <div className="kx-fees-faq__heading">
              <div className="kx-fees-eyebrow kx-fees-eyebrow--dark">
                <span />
                QUESTIONS
              </div>

              <h2>
                Frequently asked
                <br />
                <strong>questions.</strong>
              </h2>
            </div>

            <div className="kx-fees-faq__list">
              {faqs.map((item) => (
                <div key={item.id}>
                  <button
                    type="button"
                    className={`kx-fees-faq__item ${
                      openSection === item.id ? 'kx-fees-faq__item--open' : ''
                    }`}
                    onClick={() => toggleSection(item.id)}
                  >
                    <span>{item.question}</span>
                    <b>{openSection === item.id ? '−' : '+'}</b>
                  </button>

                  {openSection === item.id && (
                    <div className="kx-fees-faq__answer">{item.answer}</div>
                  )}
                </div>
              ))}
            </div>
          </section>

          {/* FINAL CTA */}
          <section className="kx-fees-final">
            <div className="kx-fees-final__orb" />

            <div className="kx-fees-final__copy">
              <div className="kx-fees-eyebrow">
                <span />
                READY WHEN YOU ARE
              </div>

              <h2>
                Your next event
                <br />
                <strong>starts here.</strong>
              </h2>

              <p>Create your organiser account and start building your event today.</p>
            </div>

            <div className="kx-fees-final__actions">
              <Link to="/organiser-registration" className="kx-fees-btn kx-fees-btn--white">
                Create organiser account
                <span>→</span>
              </Link>

              <Link to="/registration" className="kx-fees-final__secondary">
                Explore Karamale
              </Link>
            </div>
          </section>
        </div>
      </main>
    </div>
  )
}

export default FeesPage
